import { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';
import { useGetPersonalFields, useSendBat } from '../../../../hooks/Marketing';
import { toast } from 'react-toastify';

// Jetons de champ personnalisé au format legacy BeOne : #ANCHOR# (même convention que les
// codes d'offre #NUM_COUPON_x#). #SHORT_URL# est un anchor spécial auto-injecté, non éditable.
const ANCHOR_PATTERN = /#([^#]+)#/g;
const EXCLUDED_ANCHORS = new Set(['#SHORT_URL#']);

function detectUsedAnchors(content: string, availableAnchors: Set<string>): string[] {
  const found = new Set<string>();
  let match: RegExpExecArray | null;
  ANCHOR_PATTERN.lastIndex = 0;
  while ((match = ANCHOR_PATTERN.exec(content)) !== null) {
    const token = `#${match[1]}#`;
    if (availableAnchors.has(token) && !EXCLUDED_ANCHORS.has(token)) {
      found.add(token);
    }
  }
  return Array.from(found);
}

interface BatPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  content: { mode: 'unlayer' | 'html'; html: string; subject: string } | null;
}

function BatPreviewDialog({ open, onClose, content }: BatPreviewDialogProps) {
  const { sidebarColor } = useLayoutPreferences();
  const { data: personalFieldCategories } = useGetPersonalFields('EMAILING');
  const { sendBat, loading } = useSendBat();

  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState('');
  const [sender, setSender] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  const availableFields = useMemo(
    () => Object.values(personalFieldCategories).flat(),
    [personalFieldCategories]
  );

  const usedAnchors = useMemo(() => {
    if (!content) return [];
    const availableAnchors = new Set(availableFields.map(f => f.ANCHOR));
    return detectUsedAnchors(content.html, availableAnchors);
  }, [content, availableFields]);

  useEffect(() => {
    if (open) {
      setRecipients('');
      setSubject(content?.subject ?? '');
      setSender('');
      setFieldValues({});
    }
  }, [open, content]);

  const handleFieldChange = (anchor: string, value: string) => {
    setFieldValues(prev => ({ ...prev, [anchor]: value }));
  };

  const handleSend = async () => {
    const recipientList = recipients.split(';').map(r => r.trim()).filter(Boolean);
    if (recipientList.length === 0) {
      toast.warning('Veuillez renseigner au moins un destinataire.');
      return;
    }
    if (!content) return;

    let mergedHtml = content.html;
    const fields = usedAnchors.map(anchor => {
      const value = fieldValues[anchor] ?? '';
      mergedHtml = mergedHtml.split(anchor).join(value);
      return { name: anchor, value };
    });

    const ok = await sendBat({
      channel: 'EMAIL',
      message: mergedHtml,
      recipients: recipientList,
      object: subject || undefined,
      sender: sender || undefined,
      fields,
    });

    if (ok) {
      toast.success('BAT envoyé avec succès.');
      onClose();
    } else {
      toast.error("Erreur lors de l'envoi du BAT.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.75, px: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
          Renseigner le nom du/des destinataire(s)
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, '&.MuiDialogContent-root': { paddingTop: '20px' } }}>
        <TextField
          label="Destinataires"
          value={recipients}
          onChange={e => setRecipients(e.target.value)}
          helperText="Séparez les différents destinataires par un point-virgule(;)."
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            label="Objet"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Expéditeur"
            value={sender}
            onChange={e => setSender(e.target.value)}
            placeholder="BAT SERVICE"
            fullWidth
            size="small"
          />
        </Box>

        {usedAnchors.length > 0 && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
              Champ de personnalisation :
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {usedAnchors.map(anchor => (
                <TextField
                  key={anchor}
                  label={anchor}
                  value={fieldValues[anchor] ?? ''}
                  onChange={e => handleFieldChange(anchor, e.target.value)}
                  fullWidth
                  size="small"
                />
              ))}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #e9ecef', px: 3, py: 1.5 }}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon fontSize="small" />}
          onClick={handleSend}
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: sidebarColor, '&:hover': { backgroundColor: sidebarColor, opacity: 0.9 } }}
        >
          Envoyer un BAT
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default BatPreviewDialog;
